import { useRef, useState } from 'react'
import { pixelDeltaToFeet, snapToHundred } from './altitudeRibbon/geometry'

/// Vertical scrollable altitude tape. Replaces the numeric
/// altitude input in CommandPanel's Vectors section. The
/// centered value IS the target altitude; commit fires via
/// `onSubmit(altitudeFt)`.
///
/// Parent provides `key={aircraft.id}` (via the surrounding
/// CollapsibleSection) so the component remounts and `target`
/// resets to the new aircraft's `targetAltitudeFt` on selection
/// change.
export interface AltitudeRibbonProps {
  currentAltitudeFt: number
  targetAltitudeFt: number
  onSubmit: (altitudeFt: number) => void
}

// Tape geometry. Visible vertical range is ±2,000 ft from center,
// so 4,000 ft fit in the tape's height. Px-per-foot is derived
// from height / range so the tape always shows the same ft span.
const TAPE_HEIGHT_PHONE = 280 // px
const TAPE_HEIGHT_DESKTOP = 220 // px
const VISIBLE_RANGE_FT = 4000 // ±2000 from center
const PX_PER_FT_PHONE = TAPE_HEIGHT_PHONE / VISIBLE_RANGE_FT // ~0.07; used by Task 3 drag math
void PX_PER_FT_PHONE
const PX_PER_FT_DESKTOP = TAPE_HEIGHT_DESKTOP / VISIBLE_RANGE_FT // ~0.055
// Tap-vs-drag threshold in pixels.
const TAP_THRESHOLD_PX = 5

export function AltitudeRibbon(props: AltitudeRibbonProps) {
  const [target, setTarget] = useState<number>(snapToHundred(props.targetAltitudeFt))
  const svgRef = useRef<SVGSVGElement | null>(null)

  // Drag state captured at pointerdown so we can compute the
  // current target from the start position + cumulative delta.
  // Stored in a ref (not state) because we don't need a re-render
  // when these change — only on the resulting target update.
  const dragRef = useRef<{
    startClientY: number
    startTarget: number
    pointerId: number
    movedDistance: number // for tap-vs-drag distinction
  } | null>(null)

  const dirty = target !== snapToHundred(props.targetAltitudeFt)

  const inputRef = useRef<HTMLInputElement | null>(null)
  const [rawInput, setRawInput] = useState<string | null>(null)

  function focusNumericInput() {
    inputRef.current?.focus()
    inputRef.current?.select()
  }

  function handleNumericInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    // Buffer the raw text. We commit on blur (or Enter); committing
    // on every keystroke would snap the value after the first digit
    // and block 2+ digit entry — same lesson learned during the
    // heading dial implementation.
    const raw = e.target.value.replace(/\D/g, '').slice(0, 5)
    setRawInput(raw)
  }

  function commitNumericInput() {
    if (rawInput === null) return
    if (rawInput === '') {
      setRawInput(null)
      return
    }
    const n = parseInt(rawInput, 10)
    if (Number.isFinite(n)) {
      setTarget(snapToHundred(n))
    }
    setRawInput(null)
  }

  /// Compute the foot value at a given client-Y position on the
  /// tape. Used both for tap-to-snap and for the drag math
  /// (indirectly, via pixelDeltaToFeet on the cumulative delta).
  function feetAtClientY(clientY: number): number {
    const svg = svgRef.current
    if (!svg) return target
    const rect = svg.getBoundingClientRect()
    const pxPerFt = rect.height / VISIBLE_RANGE_FT
    // Distance from the center, in screen px (negative = above center).
    const offsetPx = clientY - (rect.top + rect.height / 2)
    // Drag-up-is-altitude-up convention: target up when offset is
    // negative (clientY above center).
    return target - offsetPx / pxPerFt
  }

  function handlePointerDown(e: React.PointerEvent<SVGSVGElement>) {
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = {
      startClientY: e.clientY,
      startTarget: target,
      pointerId: e.pointerId,
      movedDistance: 0,
    }
  }

  function handlePointerMove(e: React.PointerEvent<SVGSVGElement>) {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== e.pointerId) return
    const svg = svgRef.current
    if (!svg) return
    const rect = svg.getBoundingClientRect()
    const pxPerFt = rect.height / VISIBLE_RANGE_FT
    const deltaY = e.clientY - drag.startClientY
    drag.movedDistance = Math.max(drag.movedDistance, Math.abs(deltaY))
    // Subtract feet because "drag up = altitude up" — when the
    // pointer moves up (deltaY < 0), feet from pixelDeltaToFeet
    // is negative, and subtracting a negative adds to the target.
    const feet = pixelDeltaToFeet(deltaY, pxPerFt)
    setTarget(snapToHundred(drag.startTarget - feet))
  }

  function handlePointerUp(e: React.PointerEvent<SVGSVGElement>) {
    const drag = dragRef.current
    if (drag && drag.pointerId === e.pointerId) {
      // Tap (not a drag) — snap to the row at the click position.
      if (drag.movedDistance < TAP_THRESHOLD_PX) {
        setTarget(snapToHundred(feetAtClientY(e.clientY)))
      }
    }
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    dragRef.current = null
  }

  function handleWheel(e: React.WheelEvent<SVGSVGElement>) {
    // Each wheel step = ±100 ft. Prevent the page from scrolling.
    e.preventDefault()
    const direction = Math.sign(e.deltaY) // +1 = scroll down = altitude down
    if (direction === 0) return
    setTarget(snapToHundred(target - direction * 100))
  }

  // Compute the visible tick rows. Each row's Y position on the
  // tape is (target - rowFt) * pxPerFt + tapeHeight/2. We render
  // 100-ft rows from (target - 2000) to (target + 2000), labeling
  // every 1000-ft tick.
  // Use the desktop tape height for the SVG viewBox; CSS scales
  // it to the actual rendered height. (We share one set of
  // coordinates instead of rendering different tick counts per
  // breakpoint — keeps the code simple.)
  const tapeHeight = TAPE_HEIGHT_DESKTOP
  const pxPerFt = PX_PER_FT_DESKTOP
  const centerY = tapeHeight / 2
  const minVisibleFt = Math.max(0, target - VISIBLE_RANGE_FT / 2)
  const maxVisibleFt = Math.min(50000, target + VISIBLE_RANGE_FT / 2)

  // Center band spans centerY ± 18 (height 36) and contains the
  // large orange target readout. Any tick label that lands inside
  // (or within text-height of) the band would visually collide
  // with the readout. The most common case: target is itself a
  // multiple of 1000 (e.g. 12,000) — the tick at exactly target
  // ft lands at centerY and its label overlaps the readout
  // glyph-for-glyph. The line itself stays (it's a tick) but
  // its label is suppressed.
  const CENTER_BAND_HALF_H = 18
  const TEXT_HALF_H = 6 // approx. for the 10px monospace
  type Tick = { ft: number; y: number; major: boolean; labelHidden: boolean }
  const ticks: Tick[] = []
  // Start at the nearest 100-ft step above minVisibleFt.
  const startFt = Math.ceil(minVisibleFt / 100) * 100
  for (let ft = startFt; ft <= maxVisibleFt; ft += 100) {
    const y = centerY - (ft - target) * pxPerFt
    const labelHidden = Math.abs(y - centerY) < CENTER_BAND_HALF_H + TEXT_HALF_H
    ticks.push({ ft, y, major: ft % 1000 === 0, labelHidden })
  }

  // Position the current-altitude blue marker (only render when
  // it lies within the tape's visible range).
  const currentY = centerY - (props.currentAltitudeFt - target) * pxPerFt
  const currentInRange = currentY >= 0 && currentY <= tapeHeight

  return (
    <div className="relative flex flex-col items-center">
      <div className="mb-1 self-stretch text-xs uppercase tracking-wide text-slate-400">
        Set altitude
      </div>

      {/* Hidden numeric input behind the center readout. Tapping
          the readout focuses this input, which triggers the native
          iOS/Android numeric keyboard. inputMode="numeric" +
          type="text" is the most reliable pattern (type="number"
          has known leading-zero / Safari quirks). */}
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={rawInput ?? target.toLocaleString()}
        onChange={handleNumericInputChange}
        onBlur={commitNumericInput}
        onKeyDown={(e) => {
          if (e.key === 'Enter') inputRef.current?.blur()
        }}
        onFocus={(e) => e.target.select()}
        className="absolute left-1/2 top-[140px] -translate-x-1/2 w-[120px] h-[40px] opacity-0 pointer-events-none"
        aria-label="Altitude feet"
        tabIndex={-1}
      />

      {/* The tape. CSS sizes it; SVG draws the ticks + center
          band + current marker in a single coordinate space. */}
      <svg
        ref={svgRef}
        viewBox={`0 0 120 ${tapeHeight}`}
        className="w-[120px] h-[280px] md:h-[220px] touch-none select-none cursor-ns-resize rounded-md bg-slate-900 ring-1 ring-slate-700"
        aria-label="Altitude tape"
        preserveAspectRatio="none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onWheel={handleWheel}
      >
        {/* Tick rows */}
        {ticks.map((t) => (
          <g key={t.ft}>
            <line
              x1={t.major ? 60 : 75}
              y1={t.y}
              x2={120}
              y2={t.y}
              stroke={t.major ? '#94a3b8' : '#475569'}
              strokeWidth={t.major ? 1.2 : 0.6}
            />
            {t.major && !t.labelHidden && (
              <text
                x={56}
                y={t.y + 4}
                textAnchor="end"
                fill="#cbd5e1"
                fontFamily="monospace"
                fontSize="10"
              >
                {t.ft.toLocaleString()}
              </text>
            )}
          </g>
        ))}

        {/* Current-altitude blue marker on the LEFT edge. */}
        {currentInRange && (
          <g>
            <rect x="2" y={currentY - 7} width="22" height="14" rx="2" fill="#38bdf8" />
            <text
              x={13}
              y={currentY + 4}
              textAnchor="middle"
              fill="#0f172a"
              fontFamily="monospace"
              fontSize="8"
              fontWeight="bold"
            >
              {Math.round(props.currentAltitudeFt / 100) / 10}
            </text>
          </g>
        )}

        {/* Center band — orange borders, faint tint, target readout.
            The <g> stops pointer events from bubbling to the SVG's
            drag handler so tapping the readout focuses the hidden
            numeric input instead of starting a drag. */}
        <g
          onPointerDown={(e) => {
            e.stopPropagation()
            focusNumericInput()
          }}
          style={{ cursor: 'pointer' }}
        >
          <rect
            x={0}
            y={centerY - 18}
            width={120}
            height={36}
            fill="rgba(245, 158, 11, 0.08)"
            stroke="none"
          />
          <line x1={0} y1={centerY - 18} x2={120} y2={centerY - 18} stroke="#f59e0b" strokeWidth="1.5" />
          <line x1={0} y1={centerY + 18} x2={120} y2={centerY + 18} stroke="#f59e0b" strokeWidth="1.5" />
          <text
            x={60}
            y={centerY + 6}
            textAnchor="middle"
            fill="#f59e0b"
            fontFamily="monospace"
            fontSize="18"
            fontWeight="bold"
          >
            {(rawInput ?? target.toLocaleString())}
          </text>
        </g>
      </svg>

      {/* Set button. */}
      <button
        type="button"
        disabled={!dirty}
        onClick={() => props.onSubmit(target)}
        className="mt-2 w-full rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-500 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
      >
        Set {target.toLocaleString()}
      </button>
    </div>
  )
}
